import axios from 'axios';
import { useEffect, useMemo, useState } from 'react';

import { useDataModel } from '@/hooks/useDataModel';
import { useOrganization } from '@/hooks/useOrganization';
import { useUser } from '@/hooks/useUser';
import { restUrl } from '@/services/utils';
import type { DataModelAttribute, Item } from '@/types/DataModel';

import {
    applySavedValuesToItem,
    getChannelId,
    getPublicationItemCategoryOfChannel,
    getSavedPublicationValues,
    getSavedValuesForChannel,
    mergeChannelDefaultItem,
    uniqueChannelIdsWithPublicationCategory,
} from '../lib/publicationValues';
import type {
    ChannelDestination,
    CommunicationPlans,
    ItemsByChannel,
    PublicationJob,
    PublicationMode,
} from '../types';

const useChannelPublicationItems = ({
    channels,
    plans,
    mode,
    job,
    enabled,
}: {
    channels: ChannelDestination[];
    plans: CommunicationPlans;
    mode: PublicationMode;
    job?: PublicationJob;
    enabled: boolean;
}) => {
    const { dataModel } = useDataModel();
    const { organization } = useOrganization();
    const { user } = useUser();
    const [itemsByChannel, setItemsByChannel] = useState<ItemsByChannel>({});
    const [isLoading, setIsLoading] = useState(false);

    const channelsWithCategory = useMemo(
        () => uniqueChannelIdsWithPublicationCategory(channels, plans, mode),
        [channels, mode, plans],
    );
    const channelIdsKey = channelsWithCategory.map((channel) => getChannelId(channel)).join(',');

    useEffect(() => {
        if (!enabled) {
            return;
        }

        const uniqueIds = [...new Set(channelsWithCategory.map((channel) => getChannelId(channel)))];
        if (uniqueIds.length === 0) {
            setItemsByChannel({});
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const load = async () => {
            const next: ItemsByChannel = {};
            const selectedPublicationMode = getSavedPublicationValues(job)?.publication_mode;

            for (const channelId of uniqueIds) {
                const representative = channelsWithCategory.find(
                    (channel) => getChannelId(channel) === channelId,
                );
                const categoryName = getPublicationItemCategoryOfChannel(representative, plans, mode);
                if (!categoryName) {
                    continue;
                }

                let defaultItem = dataModel?.getDefaultItem(
                    categoryName,
                    organization?.id,
                    user?.id,
                );
                if (!defaultItem) {
                    const response = await axios.get<Item>(
                        restUrl(`items/defaultItem/${categoryName}`),
                    );
                    defaultItem = response.data;
                    if (defaultItem) {
                        dataModel?.setDefaultItem(
                            categoryName,
                            defaultItem,
                            organization?.id,
                            user?.id,
                        );
                    }
                }
                if (!defaultItem) {
                    continue;
                }

                const attributes: DataModelAttribute[] = dataModel?.categoryAttributes(categoryName) ?? [];
                let item = mergeChannelDefaultItem(undefined, categoryName, defaultItem, attributes);
                item = applySavedValuesToItem(
                    item,
                    getSavedValuesForChannel(channelId, job),
                    selectedPublicationMode,
                    attributes,
                );
                next[channelId] = item;
            }

            if (!cancelled) {
                setItemsByChannel(next);
                setIsLoading(false);
            }
        };

        void load();

        return () => {
            cancelled = true;
        };
        // channelIdsKey captures the representative set; job is only used for saved values.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelIdsKey, enabled, mode]);

    return {
        itemsByChannel,
        setItemsByChannel,
        isLoading,
    };
};

export { useChannelPublicationItems };
